import { useEffect, useState } from 'react';

// The dashboard theme — "green" (Almond, the default since 2026-09-24) or
// "blue" (Classic, CognitiveUX.ai) — chosen per person in Settings → General →
// Appearance. Someone who never chose gets green; an explicit "blue" is kept.
//
//   - Applied as <html data-theme="…"> — always set, for both themes;
//     themeTokens.css defines every green token under [data-theme="green"]
//     only, so "blue" is the untouched classic app.
//   - The theme is also the brand: the tab title and favicon switch between
//     Almond and CognitiveUX.ai here (brand.js has the in-app logo and name).
//   - Kept in localStorage for an instant first paint (initTheme runs before
//     React renders), and on the server (GET/PATCH
//     /api/cognitive-flows/preferences/ui, per user and account) so it follows
//     the person to another browser.
//   - useTheme() re-renders a component when the theme changes, for the few
//     colours that can't be CSS variables (SVG presentation attributes and
//     chart props) — read them with themeColor().

// The API client is loaded only when a choice is saved or synced: the sidebar
// and the sign-in pages read the theme (and the brand) without it.
async function prefsApi() {
  return (await import('../cognitive-flows/utils/api')).default;
}

export const THEMES = Object.freeze(['blue', 'green']);
export const DEFAULT_THEME = 'green';
const STORAGE_KEY = 'cux_theme';
const EVENT = 'cux-theme-change';

let current = DEFAULT_THEME;

function normalize(theme) {
  return THEMES.includes(theme) ? theme : DEFAULT_THEME;
}

function readStored() {
  try { return normalize(localStorage.getItem(STORAGE_KEY)); } catch { return DEFAULT_THEME; }
}

function writeStored(theme) {
  try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* private mode */ }
}

// Title and favicons per theme (public/ paths). Kept here rather than in
// brand.js, which imports this module.
const DOCUMENT_BRAND = Object.freeze({
  blue: { title: 'CognitiveUX.ai', icon: '/scalex_ai_logo.png', touch: '/scalex_ai_logo.png' },
  green: { title: 'Almond', icon: '/almond_favicon.png', touch: '/almond_touch.png' },
});

function applyDocumentBrand(theme) {
  const brand = DOCUMENT_BRAND[theme];
  if (!brand || typeof document === 'undefined') return;
  // Only a title that is one of the brand names is replaced: a page that set
  // its own title keeps it.
  const brandTitles = Object.values(DOCUMENT_BRAND).map((b) => b.title);
  if (!document.title || brandTitles.includes(document.title)) document.title = brand.title;
  const base = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || '';
  document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((link) => {
    link.setAttribute('href', `${base}${link.getAttribute('rel') === 'apple-touch-icon' ? brand.touch : brand.icon}`);
  });
}

/** Put a theme on the page (no persistence). */
export function applyTheme(theme) {
  current = normalize(theme);
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = current;
    applyDocumentBrand(current);
    document.dispatchEvent(new CustomEvent(EVENT, { detail: current }));
  }
  return current;
}

export function getTheme() {
  return current;
}

/** Before the first render: the theme this browser last used. */
export function initTheme() {
  return applyTheme(readStored());
}

/**
 * Change the theme: applied at once, remembered in this browser, and saved to
 * the person's preferences (best-effort — a failed save keeps the local choice).
 */
export async function setTheme(theme, { persist = true } = {}) {
  const next = applyTheme(theme);
  writeStored(next);
  if (persist) {
    try {
      await (await prefsApi()).patch('/preferences/ui', { theme: next });
    } catch {
      // Offline or signed out: the browser still remembers it.
    }
  }
  return next;
}

/** After sign-in: adopt the theme saved on the server, if it differs. */
export async function syncThemeFromServer() {
  try {
    const { data } = await (await prefsApi()).get('/preferences/ui');
    const saved = normalize(data?.theme);
    if (saved !== current) {
      applyTheme(saved);
      writeStored(saved);
    }
    return saved;
  } catch {
    return current;
  }
}

/** [theme, setTheme] — re-renders on every theme change. */
export function useTheme() {
  const [theme, setLocal] = useState(current);
  useEffect(() => {
    const onChange = (e) => setLocal(e.detail);
    document.addEventListener(EVENT, onChange);
    setLocal(current);
    return () => document.removeEventListener(EVENT, onChange);
  }, []);
  return [theme, setTheme];
}

/**
 * The value of a theme token for places CSS variables can't reach (SVG
 * presentation attributes, chart props). Falls back when the token is unset —
 * i.e. always in the classic blue theme.
 */
export function themeColor(token, fallback) {
  if (typeof document === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  return v || fallback;
}
